<div class="pd-page">

  <app-prevention-group-header activeTab="demand">
    <div class="header-controls">
      <div class="period-selector">
        @for (d of [7, 30, 90]; track d) {
          <button class="period-btn" [class.active]="periodDays() === d" (click)="setPeriodDays(d)">
            {{ 'pilotagePrevention.period.' + d | translate }}
          </button>
        }
      </div>
    </div>
  </app-prevention-group-header>

  <p class="page-subtitle">{{ 'interetPatients.header.subtitle' | translate }}</p>

  @if (loadError()) {
    <div class="cs" style="text-align:center;padding:40px 0">{{ 'interetPatients.states.error' | translate }}</div>
  } @else if (!dashboard()) {
    <div class="cs" style="text-align:center;padding:40px 0">{{ 'interetPatients.states.loading' | translate }}</div>
  } @else if (dashboard(); as d) {

    <!-- Qualité des données : ligne discrète, jamais un panneau technique -->
    <div class="quality-bar">
      <span class="quality-since">
        @if (d.demandDataAvailableFrom) {
          {{ 'interetPatients.quality.availableSince' | translate: { date: formatDate(d.demandDataAvailableFrom) } }}
        } @else {
          {{ 'interetPatients.quality.availableSinceUnknown' | translate }}
        }
      </span>
      <span class="quality-sep">·</span>
      <button
        type="button"
        class="quality-info-btn"
        [attr.aria-expanded]="qualityPanelOpen()"
        (click)="toggleQualityPanel(); $event.stopPropagation()">
        {{ 'interetPatients.quality.label' | translate }} <span aria-hidden="true">ⓘ</span>
      </button>

      @if (qualityPanelOpen()) {
        <div class="quality-panel" (click)="$event.stopPropagation()">
          <div class="quality-row-value" style="text-align:left">{{ 'interetPatients.quality.panel.title' | translate }}</div>
          <div class="cs" style="margin-top:6px">{{ 'interetPatients.quality.panel.instrumentation' | translate }}</div>
          <div class="cs" style="margin-top:6px">{{ 'interetPatients.quality.panel.reliability' | translate }}</div>
          <div class="cs" style="margin-top:6px">{{ 'interetPatients.quality.panel.untraceable' | translate }}</div>
          <hr class="quality-divider">
          <div class="quality-row-value" style="text-align:left">{{ 'interetPatients.quality.panel.thresholdTitle' | translate }}</div>
          <div class="cs" style="margin-top:6px">{{ 'interetPatients.quality.panel.thresholdExplanation' | translate: { threshold: d.smallCohortThreshold } }}</div>
          <div class="cs" style="margin-top:6px">{{ 'interetPatients.quality.panel.thresholdScope' | translate }}</div>
          <hr class="quality-divider">
          <div class="quality-row-value" style="text-align:left">{{ 'interetPatients.quality.panel.addressableTitle' | translate }}</div>
          @if (d.addressableInterestDataAvailableFrom) {
            <div class="cs" style="margin-top:6px">{{ 'interetPatients.quality.panel.addressableExplanation' | translate: { date: formatDate(d.addressableInterestDataAvailableFrom) } }}</div>
          } @else {
            <div class="cs" style="margin-top:6px">{{ 'interetPatients.quality.panel.addressableExplanationUnknown' | translate }}</div>
          }
        </div>
      }
    </div>

    <!-- Cohorte globale sous le seuil -- 2 textes distincts selon qu'une collecte a déjà commencé ou non. -->
    @if (totalsInsufficientData()) {
      <div class="empty-state-banner">
        @if (neverTrackedAnyData()) {
          <div class="empty-state-title">{{ 'interetPatients.emptyState.noData.title' | translate }}</div>
          <div class="empty-state-subtitle">{{ 'interetPatients.emptyState.noData.subtitle' | translate }}</div>
        } @else {
          <div class="empty-state-title">{{ 'interetPatients.emptyState.collecting.title' | translate }}</div>
          <div class="empty-state-subtitle">{{ 'interetPatients.emptyState.collecting.subtitle' | translate }}</div>
        }
      </div>
    }

    <!-- 1. KPI : exactement 4 cartes (lecture en entonnoir : reach -> engagement -> qualification
         commerciale -> valeur, lot "Contrat analytics Campagne" 2026-09-08) -->
    <div class="card" style="margin-bottom:20px">
      <div class="kpi-grid">
        <div class="kpi-card">
          <div class="kpi-label">
            {{ 'interetPatients.kpi.exposedSessions' | translate }}
            <span class="kpi-label-info" [attr.title]="'interetPatients.kpi.exposedSessionsHint' | translate">ⓘ</span>
          </div>
          @if (totalsInsufficientData()) {
            <div class="kpi-value kpi-value--muted">—</div>
          } @else {
            <div class="kpi-value">{{ d.exposedSessionsTotal }}</div>
          }
        </div>

        <div class="kpi-card">
          <div class="kpi-label">
            {{ 'interetPatients.kpi.interestedSessions' | translate }}
            <span class="kpi-label-info" [attr.title]="'interetPatients.kpi.interestedSessionsHint' | translate">ⓘ</span>
          </div>
          @if (totalsInsufficientData()) {
            <div class="kpi-value kpi-value--muted">—</div>
          } @else {
            <div class="kpi-value">{{ d.interestedSessionsTotal }}</div>
          }
        </div>

        <div class="kpi-card">
          <div class="kpi-label">
            {{ 'interetPatients.kpi.addressableInterests' | translate }}
            <span class="kpi-label-info" [attr.title]="'interetPatients.kpi.addressableInterestsHint' | translate">ⓘ</span>
          </div>
          @if (totalsInsufficientData()) {
            <div class="kpi-value kpi-value--muted">—</div>
          } @else {
            <div class="kpi-value" [class.kpi-value--muted]="d.addressableInterestSessionsTotal == null || (d.addressableInterestSessionsTotal === 0 && addressableDataPartiallyMissing())">{{ formatAddressableCount(d.addressableInterestSessionsTotal) }}</div>
          }
        </div>

        <div class="kpi-card">
          <div class="kpi-label">
            {{ 'interetPatients.kpi.potentialIdentified' | translate }}
            <span class="kpi-label-info" [attr.title]="'interetPatients.kpi.potentialIdentifiedHint' | translate">ⓘ</span>
          </div>
          @if (totalsInsufficientData()) {
            <div class="kpi-value kpi-value--muted">—</div>
          } @else {
            <div class="kpi-value" [class.kpi-value--muted]="d.addressablePotentialValueTotal == null || (d.addressablePotentialValueTotal === 0 && addressableDataPartiallyMissing())">{{ formatAddressableValue(d.addressablePotentialValueTotal) }}</div>
          }
        </div>
      </div>
    </div>

    @if (!totalsInsufficientData()) {

      <!-- 2. À retenir (maximum 2 insights) -->
      @if (insights().length > 0) {
        <div class="card insight-card" style="margin-bottom:20px">
          <div class="ct">{{ 'interetPatients.insight.title' | translate }}</div>
          <div class="insight-list">
            @for (insight of insights(); track $index) {
              <div class="insight-item">
                @if (insight.kind === 'topicToExplore') {
                  <div class="insight-text">{{ 'interetPatients.insight.topicToExplore' | translate: { topic: insight.topicLabel } }}</div>
                } @else if (insight.kind === 'mostAddressable') {
                  <div class="insight-text">{{ 'interetPatients.insight.mostAddressable' | translate: { topic: insight.topicLabel } }}</div>
                } @else {
                  <div class="insight-text">{{ (insight.count > 1 ? 'interetPatients.insight.noCommercialResponsePlural' : 'interetPatients.insight.noCommercialResponseSingular') | translate: { count: insight.count } }}</div>
                }
              </div>
            }
          </div>
        </div>
      }

      @if (someTopicsMaskedWhileTotalVisible()) {
        <div class="table-notice">{{ 'interetPatients.table.topicsMaskedNotice' | translate }}</div>
      }
    }

    <!-- 3. Performance des campagnes -- vue de comparaison PRINCIPALE (Lot Simplification/Déduplication,
         §6/§7) : les blocs "Sujets à potentiel" / "Sujets avec offre" ont été supprimés, ils
         répétaient les mêmes lignes déjà présentes ici. Colonne "Taux d'intérêt" retirée (ajustement
         wording, restitution uniquement) -- `demandRate`/`formatTopicRate`/le tri 'demandRate'
         restent définis côté TS (jamais supprimés, aucune logique touchée) mais ne sont plus
         déclenchables depuis ce template. Badges "Besoin de prévention"/"Sujet prévention" retirés
         (trop techniques) ; seul le badge "À potentiel" (déjà utilisé) est conservé SUR CHAQUE LIGNE,
         dans la colonne Campagne -- unique badge à porter une vraie information opérationnelle. -->
    <div class="card card--secondary">
      <div class="ct ct--secondary">{{ 'interetPatients.table.title' | translate }}</div>

      @if (totalsInsufficientData()) {
        <div class="cs" style="text-align:center;padding:20px 0">{{ 'interetPatients.table.noSessionsYet' | translate }}</div>
      } @else if (sortedTopics().length > 0) {
        <div class="topic-table">
          <div class="topic-row topic-row--header">
            <span>{{ 'interetPatients.table.columns.topic' | translate }}</span>
            <button class="topic-row-sort" (click)="toggleTopicSort('exposedSessions')" [attr.title]="'interetPatients.table.exposedTooltip' | translate">{{ 'interetPatients.table.columns.exposed' | translate }}</button>
            <button class="topic-row-sort" (click)="toggleTopicSort('interestedSessions')">{{ 'interetPatients.table.columns.interested' | translate }}</button>
            <span>{{ 'interetPatients.table.columns.addressable' | translate }}</span>
            <span>{{ 'interetPatients.table.columns.offerCurrent' | translate }}</span>
            <span>{{ 'interetPatients.table.columns.potential' | translate }}</span>
            <span></span>
          </div>
          @for (row of sortedTopics(); track row.topicCode) {
            <div class="topic-row topic-row--data">
              <span class="topic-row-name">
                {{ row.topicLabel }}
                @if (!row.insufficientData && row.offerAvailable === false && (row.interestedSessions ?? 0) > 0) {
                  <span class="badge topic-badge topic-badge--potential">{{ 'interetPatients.potential.badge' | translate }}</span>
                }
              </span>
              @if (row.insufficientData) {
                <span class="topic-row-masked">{{ 'interetPatients.states.insufficientData' | translate }}</span>
              } @else {
                <span [attr.title]="'interetPatients.table.exposedTooltip' | translate">{{ formatCount(row.exposedSessions) }}</span>
                <span>{{ formatCount(row.interestedSessions) }}</span>
                <span [attr.title]="row.addressableInterestCount == null ? ('interetPatients.table.opportunityTooltip' | translate) : null">{{ formatAddressableCount(row.addressableInterestCount) }}</span>
                <span>
                  @if (row.offerAvailable === true) {
                    <span class="badge topic-badge topic-badge--offer">{{ 'interetPatients.table.offerAvailable' | translate }}</span>
                  } @else if (row.offerAvailable === false) {
                    <span class="state-unavailable" [attr.title]="'interetPatients.table.opportunityTooltip' | translate">{{ 'interetPatients.table.offerUnavailable' | translate }}</span>
                  } @else {
                    —
                  }
                </span>
                <span [attr.title]="row.addressablePotentialValue == null ? ('interetPatients.table.opportunityTooltip' | translate) : null">{{ formatAddressableValue(row.addressablePotentialValue) }}</span>
              }
              <button type="button" class="topic-row-chevron" (click)="openCampaignForTopic(row)" [attr.aria-label]="'interetPatients.table.openCampaign' | translate: { topic: row.topicLabel }">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 18l6-6-6-6"/></svg>
              </button>
            </div>
          }
        </div>

        <div class="topic-cards">
          @for (row of sortedTopics(); track row.topicCode) {
            <div class="topic-card">
              <div class="topic-card-title">
                <span class="topic-card-title-text">
                  {{ row.topicLabel }}
                  @if (!row.insufficientData && row.offerAvailable === false && (row.interestedSessions ?? 0) > 0) {
                    <span class="badge topic-badge topic-badge--potential">{{ 'interetPatients.potential.badge' | translate }}</span>
                  }
                </span>
                <button type="button" class="topic-row-chevron" (click)="openCampaignForTopic(row)" [attr.aria-label]="'interetPatients.table.openCampaign' | translate: { topic: row.topicLabel }">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 18l6-6-6-6"/></svg>
                </button>
              </div>
              @if (row.insufficientData) {
                <div class="cs">{{ 'interetPatients.states.insufficientData' | translate }}</div>
              } @else {
                <div class="topic-card-grid">
                  <div><span class="cs">{{ 'interetPatients.table.columns.exposed' | translate }}</span><br>{{ formatCount(row.exposedSessions) }}</div>
                  <div><span class="cs">{{ 'interetPatients.table.columns.interested' | translate }}</span><br>{{ formatCount(row.interestedSessions) }}</div>
                  <div><span class="cs">{{ 'interetPatients.table.columns.addressable' | translate }}</span><br>{{ formatAddressableCount(row.addressableInterestCount) }}</div>
                  <div><span class="cs">{{ 'interetPatients.table.columns.offerCurrent' | translate }}</span><br>
                    @if (row.offerAvailable === true) {
                      {{ 'interetPatients.table.offerAvailable' | translate }}
                    } @else if (row.offerAvailable === false) {
                      {{ 'interetPatients.table.offerUnavailable' | translate }}
                    } @else { — }
                  </div>
                  <div><span class="cs">{{ 'interetPatients.table.columns.potential' | translate }}</span><br>{{ formatAddressableValue(row.addressablePotentialValue) }}</div>
                </div>
              }
            </div>
          }
        </div>
      } @else {
        <div class="cs" style="text-align:center;padding:20px 0">{{ 'interetPatients.table.empty' | translate }}</div>
      }
    </div>
  }
</div>
